import React from 'react'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import { EnrichedActionCard } from './EnrichedActionCard'
import type {
  EnrichedAction,
  SignalType,
  ActionTypeEnum,
  SentimentType,
} from '../../services/recommendations_api'
import type {
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'
import { DEFAULT_VISIBLE_ITEMS } from './constants'
import { cn } from '../UI/cn'
import {
  dispatchActionClick,
  dispatchSentimentClick,
} from './actionCardHandlers'

interface SignalSectionProps {
  /**
   * Section title (e.g., "Unreplied Emails")
   */
  title: string

  /**
   * Section description (e.g., "Inbound messages waiting for your response")
   */
  description: string

  /**
   * React icon component to display in section header
   */
  Icon: LucideIcon

  /**
   * Tailwind color class for the icon (e.g., "text-red-600")
   */
  iconColor: string

  /**
   * Whether the section is expanded (showing actions)
   */
  isExpanded: boolean

  /**
   * Callback when section header is clicked to expand/collapse
   */
  onToggleExpanded: () => void

  /**
   * Array of enriched actions to display in this section
   */
  actions: EnrichedAction[]

  /**
   * Whether to show all actions or just the first few
   */
  showAll: boolean

  /**
   * Callback when "Show More/Less" button is clicked
   */
  onToggleShowAll: () => void

  /**
   * Signal type for this section (used for click tracking)
   */
  signalType: SignalType

  /**
   * Callback when an action card is clicked
   */
  onActionClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum
  ) => void

  /**
   * Callback when user provides sentiment feedback (thumbs up/down)
   */
  onSentimentClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum,
    sentiment: SentimentType | null
  ) => void

  /**
   * Function to get existing sentiment for an action (for persistence)
   */
  getSentimentForAction?: (action: EnrichedAction) => SentimentType | null

  /**
   * Callback when user clicks Done or Dismiss on an action card
   */
  onStatusClick?: (
    action: EnrichedAction,
    status: ActionClickUpdateStatus
  ) => void

  /**
   * Function to get existing status for an action
   */
  getStatusForAction?: (action: EnrichedAction) => ActionClickStatus | null
}

/**
 * Reusable component for displaying a section of actions grouped by signal type.
 *
 * Features:
 * - Collapsible header to show/hide actions
 * - Progressive disclosure (show first 3 by default, expand to show all)
 * - Signal-specific styling (colors, icons)
 * - Click tracking integration
 */
export const SignalSection: React.FC<SignalSectionProps> = ({
  title,
  description,
  Icon,
  iconColor,
  isExpanded,
  onToggleExpanded,
  actions,
  showAll,
  onToggleShowAll,
  signalType,
  onActionClick,
  onSentimentClick,
  getSentimentForAction,
  onStatusClick,
  getStatusForAction,
}) => {
  if (!actions || actions.length === 0) {
    return null
  }

  const visibleActions = showAll
    ? actions
    : actions.slice(0, DEFAULT_VISIBLE_ITEMS)
  const hasMore = actions.length > DEFAULT_VISIBLE_ITEMS

  const handleCardClick = (action: EnrichedAction) => {
    dispatchActionClick(onActionClick, action, signalType)
  }

  const handleSentimentClick = (
    action: EnrichedAction,
    sentiment: SentimentType | null
  ) => {
    dispatchSentimentClick(onSentimentClick, action, signalType, sentiment)
  }

  return (
    <div className="rounded-lg border border-line bg-surface">
      <button
        type="button"
        onClick={onToggleExpanded}
        className="w-full p-3 flex items-center justify-between text-left"
        aria-expanded={isExpanded}
      >
        <div>
          <h3 className="text-sm font-bold text-fg flex items-center gap-1.5">
            <Icon size={18} className={iconColor} />
            {title}
            <span className="text-fg-muted font-normal">
              ({actions.length})
            </span>
          </h3>
          <p className="text-xs text-fg-secondary mt-0.5 ml-6">{description}</p>
        </div>
        <ChevronDown
          size={16}
          className={cn(
            'text-fg-muted transition-transform flex-shrink-0',
            isExpanded ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
      {isExpanded && (
        <div className="px-3 pb-3">
          <div className="space-y-2">
            {visibleActions.map((action, index) => (
              <EnrichedActionCard
                key={`${signalType}-${action.deal_id}-${index}`}
                action={action}
                onCardClick={() => handleCardClick(action)}
                onSentimentClick={(sentiment) =>
                  handleSentimentClick(action, sentiment)
                }
                initialSentiment={getSentimentForAction?.(action) ?? null}
                onStatusClick={(status) => onStatusClick?.(action, status)}
                actionStatus={getStatusForAction?.(action) ?? null}
              />
            ))}
          </div>
          {hasMore && (
            <button
              onClick={onToggleShowAll}
              className="mt-2 text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium"
            >
              {showAll
                ? 'Show Less'
                : `Show ${actions.length - DEFAULT_VISIBLE_ITEMS} More`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
